import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, formatApiError } from '../../context/AuthContext';
import { GraduationCap, User2, ClipboardList, ShieldCheck, KeyRound } from 'lucide-react';
import ZheltaLogo from '../../components/ZheltaLogo';
import AcademyFooter from '../../components/AcademyFooter';

const PORTAL_LABELS = {
  student: { icon: GraduationCap, name: 'Student Access' },
  parent: { icon: User2, name: 'Parent Access' },
  instructor: { icon: ClipboardList, name: 'Faculty Access' },
  admin: { icon: ShieldCheck, name: 'Admin Access' },
};

export default function AcademyLogin({ internalAdmin = false }) {
  const { login, loginWith2fa } = useAuth();
  const [params] = useSearchParams();
  // Internal admin login route forces portal=admin regardless of querystring.
  // Public route silently coerces ?portal=admin → student so the admin slug
  // can't leak through bookmarks or shared links.
  const rawPortal = params.get('portal') || 'student';
  const portal = internalAdmin
    ? 'admin'
    : (rawPortal === 'admin' ? 'student' : rawPortal);
  const next = params.get('next') || '/academy/dashboard';
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA challenge step state
  const [challenge, setChallenge] = useState('');
  const [code, setCode] = useState('');
  // Toggle to recovery code mode (xxxx-xxxx format) when authenticator is lost.
  const [useRecovery, setUseRecovery] = useState(false);

  const meta = PORTAL_LABELS[portal] || PORTAL_LABELS.student;
  const Icon = challenge ? KeyRound : meta.icon;

  const routeAfterAuth = (role) => {
    let dest = next;
    if (role === 'admin' && (next === '/academy/dashboard' || !next.startsWith('/admin'))) {
      try { localStorage.setItem('zhelta_admin_pw', password); } catch {/* ignore */}
      dest = '/admin?tab=academy';
    } else if (role === 'parent' && next === '/academy/dashboard') {
      dest = '/academy/parent';
    } else if (role === 'instructor' && next === '/academy/dashboard') {
      dest = '/academy/instructor';
    }
    navigate(dest, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await login(email.trim(), password);
      if (res?.requires_2fa) {
        // Backend wants a TOTP code — switch to step 2.
        setChallenge(res.challenge);
        setCode('');
        return;
      }
      routeAfterAuth(res?.user?.role || res?.role);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit2fa = async (e) => {
    e.preventDefault();
    let trimmed;
    if (useRecovery) {
      // Accept either "xxxxxxxx-xxxxxxxx" or 16 hex chars without dash.
      trimmed = (code || '').replace(/\s+/g, '').toLowerCase();
      const compact = trimmed.replace(/-/g, '');
      if (compact.length !== 16 || !/^[0-9a-f]{16}$/.test(compact)) {
        setError('Enter a recovery code in the form xxxxxxxx-xxxxxxxx.'); return;
      }
      trimmed = compact.slice(0, 8) + '-' + compact.slice(8);
    } else {
      trimmed = (code || '').replace(/\s+/g, '');
      if (trimmed.length !== 6) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    }
    setError('');
    setLoading(true);
    try {
      const res = await loginWith2fa(challenge, trimmed);
      if (res?.via === 'recovery' && typeof res.recovery_codes_remaining === 'number' && res.recovery_codes_remaining < 3) {
        try { sessionStorage.setItem('zhelta_recovery_low_warning', String(res.recovery_codes_remaining)); } catch {/* ignore */}
      }
      routeAfterAuth(res?.role);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancel2fa = () => {
    setChallenge(''); setCode(''); setError(''); setPassword(''); setUseRecovery(false);
  };

  const toggleRecovery = () => {
    setUseRecovery((v) => !v);
    setCode('');
    setError('');
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background:
          'radial-gradient(circle at 20% 20%, rgba(201,162,39,0.18), transparent 50%), radial-gradient(circle at 80% 80%, rgba(245,166,35,0.10), transparent 55%), #0b0b0e',
      }}
    >
      <div className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-[440px]">
        <Link to="/academy" className="flex items-center gap-3 justify-center mb-8">
          <ZheltaLogo size={48} />
          <div className="leading-none">
            <div className="text-[22px] font-extrabold tracking-[0.2em] text-white">ZHELTA</div>
            <div className="text-[11px] font-semibold tracking-[0.3em] text-[#f5a623]">ACADEMY</div>
          </div>
        </Link>

        <div className="bg-white rounded-2xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)] overflow-hidden">
          <div
            className="px-7 py-5 flex items-center gap-3"
            style={{ background: 'linear-gradient(135deg,#c9a227 0%,#a8871a 100%)' }}
          >
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <div className="text-[11px] font-bold tracking-[0.22em] text-white/80 uppercase">
                {challenge ? 'Two-factor authentication' : 'Sign in to'}
              </div>
              <div className="text-[18px] font-extrabold text-white">{challenge ? 'Enter verification code' : meta.name}</div>
            </div>
          </div>

          {!challenge && (
            <form onSubmit={onSubmit} className="px-7 py-6 space-y-4">
              <div>
                <label className="block text-[11px] font-bold tracking-[0.18em] uppercase text-[#6E6E73] mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="w-full px-4 py-2.5 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] focus:ring-2 focus:ring-[#c9a227]/30 outline-none text-[15px]"
                  placeholder="you@example.com"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold tracking-[0.18em] uppercase text-[#6E6E73] mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="w-full px-4 py-2.5 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] focus:ring-2 focus:ring-[#c9a227]/30 outline-none text-[15px]"
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <div data-testid="login-error" className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px] px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[14px] font-bold uppercase tracking-[0.18em] text-[#0e0e10] disabled:opacity-60"
                style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 50%,#a8871a 100%)' }}
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>

              <div className="flex justify-between items-center pt-2 text-[12.5px]">
                <Link to="/academy/forgot-password" className="text-[#c9a227] hover:underline font-semibold">
                  Forgot password?
                </Link>
                <Link to="/academy" className="text-[#6E6E73] hover:text-[#c9a227]">
                  ← Back to Academy
                </Link>
              </div>
            </form>
          )}

          {challenge && (
            <form onSubmit={onSubmit2fa} className="px-7 py-6 space-y-4" data-testid="login-2fa-form">
              {!useRecovery && (
                <p className="text-[13px] text-[#6E6E73] leading-relaxed">
                  Open <span className="font-semibold text-[#0e0e10]">Microsoft Authenticator</span> or{' '}
                  <span className="font-semibold text-[#0e0e10]">Google Authenticator</span> and enter the
                  current 6-digit code for your ZHELTA Admin account.
                </p>
              )}
              {useRecovery && (
                <p className="text-[13px] text-[#6E6E73] leading-relaxed">
                  Lost access to your authenticator? Enter one of your saved <strong>recovery codes</strong>.
                  Each code can be used <strong>once</strong>.
                </p>
              )}

              <div>
                <label className="block text-[11px] font-bold tracking-[0.18em] uppercase text-[#6E6E73] mb-1.5">
                  {useRecovery ? 'Recovery code' : 'Authentication code'}
                </label>
                {useRecovery ? (
                  <input
                    type="text"
                    autoComplete="off"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^0-9a-f-]/g, '').slice(0, 17))}
                    data-testid="login-2fa-recovery-code"
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] focus:ring-2 focus:ring-[#c9a227]/30 outline-none text-center text-[18px] tracking-[0.18em] font-mono"
                    placeholder="xxxxxxxx-xxxxxxxx"
                    autoFocus
                  />
                ) : (
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                    data-testid="login-2fa-code"
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] focus:ring-2 focus:ring-[#c9a227]/30 outline-none text-center text-[24px] tracking-[0.5em] font-mono"
                    placeholder="000000"
                    autoFocus
                  />
                )}
              </div>

              {error && (
                <div data-testid="login-2fa-error" className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px] px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || (useRecovery ? code.replace(/-/g, '').length !== 16 : code.length !== 6)}
                data-testid="login-2fa-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[14px] font-bold uppercase tracking-[0.18em] text-[#0e0e10] disabled:opacity-60"
                style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 50%,#a8871a 100%)' }}
              >
                {loading ? 'Verifying…' : 'Verify & sign in'}
              </button>

              <button
                type="button"
                onClick={toggleRecovery}
                data-testid="login-2fa-toggle-recovery"
                className="w-full text-[12.5px] text-[#c9a227] hover:underline font-semibold"
              >
                {useRecovery ? '← Use authenticator code instead' : 'Use a recovery code instead'}
              </button>

              <button
                type="button"
                onClick={cancel2fa}
                data-testid="login-2fa-cancel"
                className="w-full text-[12.5px] text-[#6E6E73] hover:text-[#c9a227]"
              >
                ← Use a different account
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-[12px] text-white/50 mt-5">
          Access is provisioned after enrollment. Need help?{' '}
          <a href="mailto:careers@zhelta.com" className="text-[#f5a623] hover:underline">careers@zhelta.com</a>
        </p>
      </div>
      </div>
      <AcademyFooter />
    </div>
  );
}
